import { api, ensureCsrfCookie } from '@/api/client';
import type { CursorPage } from '@/api/commercial';
import type { ApprovalData } from '@/types/generated';

/**
 * The approval queue.
 *
 * `scope` defaults to `mine` on the server, and this client does not override it. An approver
 * opening the page wants the handful of decisions waiting on them; everything the organisation
 * has ever recorded is a different question, asked deliberately.
 */

export interface ApprovalFilters {
  /** `mine` (default) is what is actionable by the caller; `all` is the whole organisation. */
  scope?: 'mine' | 'all' | undefined;
  status?: string | undefined;
  outstandingOnly?: boolean | undefined;
  cursor?: string | null | undefined;
}

export async function fetchApprovals(
  filters: ApprovalFilters = {},
): Promise<CursorPage<ApprovalData>> {
  const { data } = await api.get<CursorPage<ApprovalData>>('/approvals', {
    params: pruned({
      scope: filters.scope,
      status: filters.status,
      outstanding_only: filters.outstandingOnly === false ? 0 : undefined,
      cursor: filters.cursor ?? undefined,
    }),
  });
  return data;
}

/**
 * Approve or reject.
 *
 * One call with two outcomes rather than two endpoints, mirroring the API: it is a single
 * decision, and a client choosing between two URLs would be choosing on this same field.
 * `comments` is required by the server on a rejection — and by a database constraint behind it.
 */
export async function decideApproval(
  id: string,
  decision: 'approved' | 'rejected',
  comments?: string,
): Promise<ApprovalData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ approval: ApprovalData }>(`/approvals/${id}/decide`, {
    decision,
    comments,
  });
  return data.approval;
}

/**
 * Hand one step to a colleague.
 *
 * The step keeps the role it required, so the audit still shows what level of authority the
 * decision needed — only who may exercise it moves, and only for this request.
 */
export async function delegateApproval(id: string, userId: string): Promise<ApprovalData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ approval: ApprovalData }>(`/approvals/${id}/delegate`, {
    user_id: userId,
  });
  return data.approval;
}

function pruned(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== '' && value !== null,
    ),
  );
}
